import { useContext } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ShiftEvent, type IAddShiftEvent, type IShiftData, ShiftCategory, TaskCategory } from "@/logic/api";
import { SessionContext } from "@/utility/useSession";
import { useApiWithReject } from "@/utility/utils";

export const useAddShiftEvent = () => {
    const { current: session } = useContext(SessionContext);
    const queryClient = useQueryClient();
    const addShiftEventApi = useApiWithReject(api.hooks.shift.addShiftEvent());

    const userUuid = session?.site?.user?.uuid;
    const tenantId = session?.site?.tenant?.id;

    return useMutation<ShiftEvent, Error, IAddShiftEvent>({
        mutationFn: async (payload) => {
            if (!session?.context) {
                throw new Error("Session context not available");
            }
            return addShiftEventApi.send(session.context, payload);
        },
        onSuccess: (data, variables) => {
            //TODO: DD - Update cache directly - refetching the whole list was causing problems which were more difficult to fix for now
            queryClient.setQueryData<IShiftData>(
                ["shift", "data", userUuid, tenantId],
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        data: old.data.map((shift) => {
                            // Shift start/end
                            if (data.category === ShiftCategory.Shift && shift.user.uuid === variables.userUuid) {
                                const isShiftEnd = data.shift.endDate != null;

                                if (!isShiftEnd) {
                                    // Shift started
                                    return {
                                        ...shift,
                                        shiftUuid: data.shift.uuid,
                                        carPark: { uuid: data.shift.carPark.uuid, name: data.shift.carPark.name },
                                        period: {
                                            startDate: data.shift.startDate,
                                            endDate: null,
                                        },
                                        lastBreak: {
                                            startDate: null,
                                            endDate: null,
                                        },
                                        currentAssignment: null,
                                    };
                                } else {
                                    // Shift ended
                                    return {
                                        ...shift,
                                        shiftUuid: null,
                                        period: {
                                            startDate: data.shift.startDate,
                                            endDate: data.shift.endDate ?? null,
                                        },
                                        lastBreak: {
                                            startDate: null,
                                            endDate: null,
                                        },
                                    };
                                }
                            }

                            // Break and task assignment
                            if (shift.shiftUuid === variables.shiftUuid) {
                                if (data.category === ShiftCategory.Break) {
                                    const isBreakEnd = variables.action === TaskCategory.Off;

                                    // Break category
                                    if (!isBreakEnd) {
                                        return {
                                            ...shift,
                                            lastBreak: {
                                                startDate: data.occurredAt,
                                                endDate: null,
                                            },
                                        };
                                    } else {
                                        return {
                                            ...shift,
                                            lastBreak: {
                                                ...shift.lastBreak,
                                                endDate: data.occurredAt,
                                            },
                                        };
                                    }
                                } else if (data.category === ShiftCategory.Assignment) {
                                    // Assignment/task category
                                    return {
                                        ...shift,
                                        currentAssignment: {
                                            eventCategory: data.category,
                                            eventAction: data.action,
                                            occurredAt: data.occurredAt,
                                        },
                                    };
                                }
                            }
                            return shift;
                        }),
                    };
                }
            );
        },
    });
};
